import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCheck, FiX, FiInfo } = FiIcons;

const Comparison = () => {
  const comparisonData = [
    {
      category: "What It Does",
      weTheBillions: "Trains brain's sleep system",
      sleepApps: "Plays sounds, tracks sleep",
      melatonin: "Sleep hormone supplement",
      traditionalTherapy: "Behavior modification"
    },
    {
      category: "Root Cause Fix",
      weTheBillions: { icon: FiCheck, text: "Strengthens executive functions", type: "success" },
      sleepApps: { icon: FiX, text: "Surface-level only, one size fits all", type: "error" },
      melatonin: { icon: FiX, text: "No behavioral change", type: "error" },
      traditionalTherapy: { icon: FiInfo, text: "Limited cognitive focus", type: "warning" }
    },
    {
      category: "Time to Results",
      weTheBillions: "2-4 weeks",
      sleepApps: "Immediate but temporary",
      melatonin: "30-60 minutes",
      traditionalTherapy: "8-12 weeks"
    },
    {
      category: "Works Without Props",
      weTheBillions: { icon: FiCheck, text: "Build lasting skills", type: "success" },
      sleepApps: { icon: FiX, text: "Need phone/device", type: "error" },
      melatonin: { icon: FiX, text: "Need nightly supplement", type: "error" },
      traditionalTherapy: { icon: FiInfo, text: "Moderate independence", type: "warning" }
    },
    {
      category: "Improves Daytime Performance",
      weTheBillions: { icon: FiCheck, text: "20% minimum cognitive boost", type: "success" },
      sleepApps: { icon: FiX, text: "Sleep tracking only", type: "error" },
      melatonin: { icon: FiX, text: "May cause grogginess", type: "error" },
      traditionalTherapy: { icon: FiInfo, text: "Indirect benefits", type: "warning" }
    },
    {
      category: "Investment",
      weTheBillions: "$135-200/session",
      sleepApps: "$5-15/month",
      melatonin: "$10-30/month",
      traditionalTherapy: "$100-300/session"
    },
    {
      category: "Insurance Coverage",
      weTheBillions: "Often",
      sleepApps: "No",
      melatonin: "No",
      traditionalTherapy: "Often"
    },
    {
      category: "Side Effects",
      weTheBillions: "None",
      sleepApps: "Blue light exposure",
      melatonin: "Melatonin hangover",
      traditionalTherapy: "None"
    },
    {
      category: "Long-term Success",
      weTheBillions: { icon: FiCheck, text: "Skills for life", type: "success" },
      sleepApps: { icon: FiX, text: "App-dependent", type: "error" },
      melatonin: { icon: FiX, text: "Costly", type: "error" },
      traditionalTherapy: { icon: FiInfo, text: "Mixed durability", type: "warning" }
    }
  ];

  const renderCell = (data) => {
    if (typeof data === 'string') {
      return <span className="text-gray-700 font-raleway">{data}</span>;
    }

    const iconColor = {
      success: 'text-green-600',
      error: 'text-red-600', 
      warning: 'text-yellow-600'
    }[data.type];

    return (
      <div className="flex items-center gap-2">
        <SafeIcon icon={data.icon} className={`w-5 h-5 ${iconColor}`} />
        <span className="text-gray-700 font-raleway text-sm">{data.text}</span>
      </div>
    );
  };

  return (
    <section id="comparison" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl lg:text-5xl font-playfair font-bold text-gray-900 mb-8">
            We The Billions vs. Everything Else You've Tried
          </h2>
        </motion.div>

        {/* Desktop Table */}
        <div className="hidden lg:block">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="bg-white rounded-3xl shadow-xl overflow-hidden"
          >
            <table className="w-full">
              <thead>
                <tr className="bg-gradient-to-r from-accent-magenta to-accent-purple text-white">
                  <th className="text-left p-6 font-playfair font-bold text-lg">Feature</th>
                  <th className="text-left p-6 font-playfair font-bold text-lg">We The Billions</th>
                  <th className="text-left p-6 font-playfair font-bold text-lg">Sleep Apps</th>
                  <th className="text-left p-6 font-playfair font-bold text-lg">Melatonin</th>
                  <th className="text-left p-6 font-playfair font-bold text-lg">Traditional Sleep Therapy</th>
                </tr>
              </thead>
              <tbody>
                {comparisonData.map((row, index) => (
                  <motion.tr
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className={`${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'} hover:bg-accent-light/30 transition-colors duration-200`}
                  >
                    <td className="p-6 font-semibold text-gray-900 font-raleway">{row.category}</td>
                    <td className="p-6">{renderCell(row.weTheBillions)}</td>
                    <td className="p-6">{renderCell(row.sleepApps)}</td>
                    <td className="p-6">{renderCell(row.melatonin)}</td>
                    <td className="p-6">{renderCell(row.traditionalTherapy)}</td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </motion.div>
        </div>

        {/* Mobile Cards */}
        <div className="lg:hidden space-y-8">
          {comparisonData.map((row, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl shadow-lg p-6"
            >
              <h3 className="font-playfair font-bold text-xl text-gray-900 mb-6 text-center">
                {row.category}
              </h3>
              <div className="space-y-4">
                <div className="p-4 bg-accent-magenta/10 rounded-xl">
                  <div className="font-semibold text-accent-magenta mb-2">We The Billions</div>
                  {renderCell(row.weTheBillions)}
                </div>
                <div className="p-4 bg-gray-50 rounded-xl">
                  <div className="font-semibold text-gray-700 mb-2">Sleep Apps</div>
                  {renderCell(row.sleepApps)}
                </div>
                <div className="p-4 bg-gray-50 rounded-xl">
                  <div className="font-semibold text-gray-700 mb-2">Melatonin</div>
                  {renderCell(row.melatonin)}
                </div>
                <div className="p-4 bg-gray-50 rounded-xl">
                  <div className="font-semibold text-gray-700 mb-2">Traditional Sleep Therapy</div>
                  {renderCell(row.traditionalTherapy)}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Comparison;